import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, ArrowRight, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Transfers() {
  const [transfers, setTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    from_branch_id: "",
    to_branch_id: "",
    notes: "",
    items: [],
  });
  const [selProduct, setSelProduct] = useState("");

  const load = async () => {
    const [t, b, p] = await Promise.all([
      api.get("/transfers"),
      api.get("/branches"),
      api.get("/products"),
    ]);
    setTransfers(t.data);
    setBranches(b.data);
    setProducts(p.data);
  };
  useEffect(() => {
    load();
  }, []);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const addProductRow = () => {
    if (!selProduct) return;
    if (form.items.find((i) => i.product_id === selProduct)) {
      toast.message("Already added");
      return;
    }
    setForm((f) => ({
      ...f,
      items: [...f.items, { product_id: selProduct, quantity: 1 }],
    }));
    setSelProduct("");
  };

  const save = async () => {
    if (!form.from_branch_id || !form.to_branch_id) {
      toast.error("Select both branches");
      return;
    }
    if (form.from_branch_id === form.to_branch_id) {
      toast.error("Branches must differ");
      return;
    }
    if (form.items.length === 0) {
      toast.error("Add at least one product");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/transfers", {
        ...form,
        items: form.items.map((i) => ({
          ...i,
          quantity: Number(i.quantity) || 1,
        })),
      });
      toast.success("Transfer completed");
      setOpen(false);
      setForm({ from_branch_id: "", to_branch_id: "", notes: "", items: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const productName = (id) => products.find((p) => p.id === id)?.name || id;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="transfers-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Operations
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Branch Transfers
          </h1>
        </div>
        <Button
          data-testid="new-transfer-btn"
          onClick={() => setOpen(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> New transfer
        </Button>
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Date</TableHead>
              <TableHead>Route</TableHead>
              <TableHead className="text-right">Items</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transfers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-10 text-muted-foreground"
                >
                  No transfers yet.
                </TableCell>
              </TableRow>
            ) : (
              transfers.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(t.created_at).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell>
                    <span>
                      {branchName(t.from_branch_id)}{" "}
                      <ArrowRight size={12} className="inline mx-1 text-gold" />{" "}
                      {branchName(t.to_branch_id)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {t.items.length}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {t.notes || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              New Branch Transfer
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                From branch
              </Label>
              <Select
                value={form.from_branch_id}
                onValueChange={(v) => setForm({ ...form, from_branch_id: v })}
              >
                <SelectTrigger
                  data-testid="transfer-from"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                To branch
              </Label>
              <Select
                value={form.to_branch_id}
                onValueChange={(v) => setForm({ ...form, to_branch_id: v })}
              >
                <SelectTrigger
                  data-testid="transfer-to"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2 mt-4">
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              Items
            </Label>
            <div className="flex gap-2">
              <Select value={selProduct} onValueChange={setSelProduct}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Add product" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {p.product_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={addProductRow}
                variant="ghost"
                className="text-gold"
              >
                <Plus size={14} />
              </Button>
            </div>
            <div className="rounded-lg border border-border max-h-48 overflow-y-auto">
              {form.items.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No items added.
                </div>
              ) : (
                form.items.map((it, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-2 border-b border-border last:border-0"
                  >
                    <div className="text-sm">{productName(it.product_id)}</div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        className="h-8 w-20 bg-background border-border text-right tabular-nums"
                        value={it.quantity}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            items: f.items.map((x, idx) =>
                              idx === i
                                ? { ...x, quantity: e.target.value }
                                : x,
                            ),
                          }))
                        }
                      />
                      <button
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            items: f.items.filter((_, idx) => idx !== i),
                          }))
                        }
                        className="text-muted-foreground hover:text-destructive p-1"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              Notes
            </Label>
            <Input
              className="mt-1.5 bg-background border-border"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-transfer-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
